import api from "../api";
import { FormJSON, removeServiceData } from "exportForm/exportForm";
import { IdData } from "hooks/FormDesigner/useElementList";
import { PageData } from "hooks/FormDesigner/useDefinitionsList";
import { AxiosRequestConfig } from "axios";

/**
 * Gets list of ID Definitions for NestedView or MultiReferneceView
 */

type definitionId = { id: string };

const getDefinitions = async (): Promise<IdData> => {
	const req = await api.request({
		url: "forms/definitions",
		method: "GET",
	});
	const definitions: IdData = [];
	req.data.matches
		.sort((a: definitionId, b: definitionId) => {
			return a.id.toLowerCase().localeCompare(b.id.toLowerCase());
		})
		.forEach((definition: definitionId) => {
			definitions.push({
				label: definition.id.substr(definition.id.lastIndexOf("/") + 1),
				value: definition.id,
			});
		});

	return Promise.resolve(definitions);
};

const getPaginatedDefinitions = async (params: {
	name: string;
	limit: number;
	offset: number;
}): Promise<[IdData, boolean]> => {
	const req = await api.request({
		url: `forms/definitions?${
			params?.name !== undefined && params.name.trim() !== ""
				? `name=${params.name}&`
				: ""
		}limit=${params?.limit}&offset=${params?.offset}`,
		method: "GET",
	});
	const definitions: IdData = [];
	req.data.matches
		.sort((a: definitionId, b: definitionId) => {
			return a.id.toLowerCase().localeCompare(b.id.toLowerCase());
		})
		.forEach((definition: definitionId) => {
			definitions.push({
				label: definition.id.substr(definition.id.lastIndexOf("/") + 1),
				value: definition.id,
			});
		});

	return Promise.resolve([
		definitions,
		req.data["@controls"] !== undefined &&
			req.data["@controls"]["forms:nextPage"] !== undefined,
	]);
};

/**
 * Get a list of definitions
 *
 * @param opts optional axios request params
 */
const getDefinitionsList = async (params: {
	offset: number;
	limit: number;
	name?: string;
}) => {
	const res = await api.request({
		method: "GET",
		url: `forms/definitions?${
			params.name !== undefined && params.name.trim() !== ""
				? `name=${params.name}&`
				: ""
		}limit=${params.limit}&offset=${params.offset}`,
	});

	const data: PageData = {
		hasMore: false,
		results: res.data.matches,
		totalCount: res.data.totalCount,
	};

	return data;
};

/**
 * Runs validation on form using forms service
 *
 * @param value - form to validate
 */
const runFormValidation = async (value: FormJSON): Promise<boolean> => {
	const req = await api.request({
		url: "forms/definitions",
		method: "POST",
		data: value,
	});
	return Promise.resolve(req.data);
};

/**
 * Uploads form to forms service
 *
 * @param value - Form json to upload
 * @param formId - Id of form to upload
 */
const uploadForm = async (
	value: FormJSON,
	formId: string
): Promise<boolean> => {
	const req = await api.request({
		url: `forms/definitions/${formId}`,
		method: "PUT",
		data: value,
	});
	return Promise.resolve(req.status === 200 ? true : false);
};

/**
 * Gets whether form definition exists in database
 *
 * @param formId - Id of form to check
 * @param customOpts - custom optional params like handleErrors
 */
const getDefinition = async (
	formId: string,
	customOpts?: AxiosRequestConfig["customOpts"]
): Promise<boolean> => {
	let result = false;
	await api
		.request({
			url: `forms/definitions/${formId}`,
			method: "GET",
			customOpts,
		})
		.then(req => {
			if (req.status === 200) {
				result = true;
			} else {
				result = false;
			}
		})
		.catch(() => {
			result = false;
		});
	return Promise.resolve(result);
};

/**
 * Gets form Definition Json from forms service
 *
 * @param formId - Id of form
 */
const getFormDefinition = async (
	formId: string
): Promise<FormJSON | undefined> => {
	let result: FormJSON | undefined = undefined;
	await api
		.request({
			url: `forms/definitions/${formId}`,
			method: "GET",
		})
		.then(req => {
			if (req.status === 200 && req.data !== undefined) {
				const finalData = removeServiceData(req.data);
				result = finalData as FormJSON;
			} else {
				result = undefined;
			}
		})
		.catch(() => {
			result = undefined;
		});
	return Promise.resolve(result);
};

/**
 * Deletes form definition from the forms service
 *
 * @param formId - Id of form to delete
 */
// TODO: With some of these api calls why not catch errors in the code that calls this and save yourself some hassle? Request returns a promise anyway
// TODO: check your other calls for similar refactoring, example below:
/*
	const deleteDefinition = async(fid: string) => api.request({ url: '', method: "DELETE" });

	*Then to call it elsewhere in your code:*

	try {
		await deleteDefinition("123");
	}
	catch(error) {}

*/
const deleteDefinition = async (formId: string): Promise<boolean> => {
	let result = false;
	await api
		.request({
			url: `forms/definitions/${formId}`,
			method: "DELETE",
		})
		.then(req => {
			if (req.status === 200) {
				result = true;
			}
		})
		.catch(() => {
			result = false;
		});
	return Promise.resolve(result);
};

export default {
	getDefinitions,
	getPaginatedDefinitions,
	runFormValidation,
	uploadForm,
	getDefinition,
	getDefinitionsList,
	getFormDefinition,
	deleteDefinition,
};
